// Ansicht Anlagenschema.
//
// Linke Palette: Symbole/Sensoren aus GET /palette/symbols, nach Domäne filterbar. Symbole per
// Klick auf die Canvas setzen. Rechtes Panel: bei Sensoren die erhobenen Variablen mit Einheit,
// Wertebereich und optionalen Tag-/OPC-Feldern pflegen. Diese Variablen ergeben den Signalkatalog.
//
// Das Anlagenschema (platzierte Symbole + Positionen + Verrohrung) wird als eigenes Projekt-
// Artefakt persistiert: beim Projektwechsel baut die Ansicht die Canvas aus GET /schematic wieder
// auf, „Schema & Katalog speichern" schreibt beides — das Schema über PUT /projects/{id}/schematic
// (schematic.schema.json) und den daraus abgeleiteten Katalog über PUT /projects/{id}/catalog
// (signal-catalog.schema.json). So bleibt die Kopplung erhalten: der Katalog entsteht weiterhin aus
// den Sensor-/Aktor-Variablen der platzierten Symbole.

import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import {
  Background,
  Controls,
  MarkerType,
  ReactFlow,
  useEdgesState,
  useNodesState,
  type Edge,
  type Node,
} from '@xyflow/react';
import { useI18n } from '../i18n';
import { useStore } from '../state/store';
import { getSymbols, putCatalog, putGraph, putSchematic, stampGraph } from '../api/client';
import type {
  CatalogVariable,
  Direction,
  OpcAccess,
  OpcDatatype,
  OpcQualityHandling,
  PaletteSymbol,
  PlantSchematic,
  SchematicNode,
  SignalCatalog,
  StampVariable,
  SymbolVariable,
} from '../api/types';
import { PlantNode, type PlantNodeData } from '../components/nodes';
import { PlantSymbolIcon } from '../components/plantSymbols';
import { schematicEdgeAttrs } from '../graph/streams';
import { Button, EmptyState, LabeledInput, LabeledSelect } from '../components/ui';
import { CanvasDrawerBar, SidePanel, useNarrowViewport } from '../components/responsive';
import { UaBrowseOnboardingDialog } from './UaBrowseOnboardingDialog';
import { OnboardingWizardDialog } from './OnboardingWizardDialog';

const nodeTypes = { plant: PlantNode };

// Baut aus einer Verrohrungskante ({from,to,stream}) die stromtyp-gestylte React-Flow-Kante:
// eingefärbt nach Medium, mit Richtungspfeil und dezentem Stream-Label. Die Handles (pipe-out →
// pipe-in) sorgen dafür, dass die Kante an den Symbolen andockt und überhaupt gezeichnet wird. Die
// Attribute stammen aus graph/streams (dependency-frei testbar); hier wird nur der typisierte
// Marker-Enum gesetzt.
function schematicEdge(
  edge: { id?: string; from: string; to: string; stream?: string },
  index: number,
  lang: 'de' | 'en',
): Edge {
  const attrs = schematicEdgeAttrs(edge, index, lang) as Edge;
  return { ...attrs, markerEnd: { ...(attrs.markerEnd as object), type: MarkerType.ArrowClosed } };
}

// Ein Sensor-Node auf der Canvas erzeugt je Standard-Variable eine Katalog-Variable. Wir halten
// den entstehenden Katalog getrennt vom Canvas-Zustand und referenzieren die Symbol-Instanz über
// die Node-ID (source.symbol = "<symbolId>@<nodeId>").
interface DraftVariable extends CatalogVariable {
  nodeId: string; // Canvas-Instanz, aus der die Variable stammt
}

const DATATYPES: OpcDatatype[] = ['Float', 'Double', 'Int16', 'UInt16', 'Boolean', 'String'];

// Baut aus einer Symbol-Standard-Variable eine Katalog-Variable (Draft) mit sprechendem Tag.
function draftFromVariable(
  sym: PaletteSymbol,
  nodeId: string,
  v: SymbolVariable,
  index: number,
): DraftVariable {
  const isa = sym.isa_tag ?? 'AT';
  const range = v.range ?? [0, 100];
  return {
    nodeId,
    tag: `${isa}-${v.name}-${index + 1}`,
    name: v.name,
    quantity: v.quantity,
    unit: v.unit,
    range: { min: range[0], max: range[1] },
    direction: (sym.category === 'aktor_sollwert' ? 'output' : 'input') as Direction,
    source: { symbol: `${sym.id}@${nodeId}`, variable: v.name },
  };
}

// Reihenfolge der Symbol-Kategorien in der Palette (fachlich sortiert: Behälter, Maschinen,
// Sensoren, Aktoren/Sollwerte, Ströme). Unbekannte Kategorien landen hinten.
const SYMBOL_CATEGORY_ORDER = ['behaelter', 'maschine', 'sensor', 'aktor_sollwert', 'strom'];

export function PlantView(): JSX.Element {
  const { t, lang } = useI18n();
  const {
    activeProject,
    activeProjectId,
    catalog,
    setCatalog,
    schematic,
    setSchematic,
    notify,
    reportError,
    bumpReload,
  } = useStore();

  // UA-Browse-Onboarding-Assistent (#152): read-only Browse → Kandidaten kuratieren → Katalog.
  const [uaBrowseOpen, setUaBrowseOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  const [symbols, setSymbols] = useState<PaletteSymbol[]>([]);
  // Domänen-Filter (Issue #330): MEHRERE Domänen gleichzeitig. Leer = alle — das ist der
  // Default und kein Sonderfall: ohne Auswahl wird nichts ausgeblendet.
  const [domainFilter, setDomainFilter] = useState<string[]>([]);
  const [paletteSearch, setPaletteSearch] = useState<string>('');
  const [loadingPalette, setLoadingPalette] = useState(false);
  // Symbol-Metadaten (id → Symbol) über alle Domänen, damit ein geladenes Schema seine Symbole
  // rendern kann, auch wenn der aktuelle Domänenfilter sie nicht in der Palette zeigt.
  const [symbolById, setSymbolById] = useState<Record<string, PaletteSymbol>>({});

  // Die auswählbaren Domänen kommen aus dem SYMBOLKATALOG selbst (Vereinigung über
  // `symbol.domains[]`), nicht aus einer Konstante im Frontend: eine neue Domäne im Katalog soll
  // ohne Frontend-Änderung in der Leiste auftauchen (Issue #330).
  const availableDomains = useMemo((): string[] => {
    const seen = new Set<string>();
    for (const s of symbols) for (const d of s.domains ?? []) seen.add(d);
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [symbols]);

  // Palette: nach Domäne UND Suchtext filtern (Name DE/EN, ISA-Tag, Kategorie), dann nach
  // Kategorie gruppieren. Beide Filter wirken clientseitig und verknüpft (UND): ein Symbol muss
  // zu mindestens einer gewählten Domäne gehören und zum Suchtext passen.
  const groupedSymbols = useMemo((): { category: string; items: PaletteSymbol[] }[] => {
    const q = paletteSearch.trim().toLowerCase();
    const match = (s: PaletteSymbol): boolean => {
      if (!q) return true;
      return [s.label, s.label_en, s.isa_tag ?? '', s.category, s.id]
        .some((v) => v.toLowerCase().includes(q));
    };
    // Leere Auswahl = alle Domänen. Ein Symbol ohne Domänen-Angabe (Stromtypen, Sollwerte)
    // bleibt sichtbar, solange nicht gefiltert wird, und verschwindet bei aktiver Auswahl —
    // es gehört dann zu keiner der gewählten Domänen.
    const inDomain = (s: PaletteSymbol): boolean =>
      domainFilter.length === 0 || (s.domains ?? []).some((d) => domainFilter.includes(d));
    const byCat = new Map<string, PaletteSymbol[]>();
    for (const s of symbols) {
      if (!inDomain(s) || !match(s)) continue;
      const list = byCat.get(s.category) ?? [];
      list.push(s);
      byCat.set(s.category, list);
    }
    const order = (c: string): number => {
      const i = SYMBOL_CATEGORY_ORDER.indexOf(c);
      return i === -1 ? SYMBOL_CATEGORY_ORDER.length : i;
    };
    return [...byCat.entries()]
      .sort((a, b) => order(a[0]) - order(b[0]) || a[0].localeCompare(b[0]))
      .map(([category, items]) => ({ category, items }));
  }, [symbols, paletteSearch, domainFilter]);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node<PlantNodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Offen/zu der beiden Seitenpanele auf dem Handy (Issue #479). Am Schreibtisch stehen die
  // Panele ohnehin fest in ihrer Spalte, dort hat der Zustand keine Wirkung.
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [propsOpen, setPropsOpen] = useState(false);
  const narrow = useNarrowViewport();
  const [variables, setVariables] = useState<DraftVariable[]>([]);
  const [saving, setSaving] = useState(false);
  const [stamping, setStamping] = useState(false);

  // Palette EINMAL vollständig laden — ohne Domänenparameter. Seit #330 filtert die Palette
  // clientseitig und mehrfach; ein serverseitiger Filter je Auswahl wäre ein Request pro Klick
  // und würde die Symbol-Metadaten je Filterstand wieder lückenhaft machen. `symbolById` trägt
  // dieselbe vollständige Liste, damit ein geladenes Schema seine Symbole rendern kann, auch
  // wenn der Filter sie in der Palette gerade versteckt.
  useEffect(() => {
    let cancelled = false;
    setLoadingPalette(true);
    getSymbols()
      .then((res) => {
        if (cancelled) return;
        setSymbols(res.symbols);
        setSymbolById((prev) => {
          const next = { ...prev };
          for (const s of res.symbols) next[s.id] = s;
          return next;
        });
      })
      .catch((err) => !cancelled && reportError(err, t('plant.paletteFailed')))
      .finally(() => !cancelled && setLoadingPalette(false));
    return () => {
      cancelled = true;
    };
  }, [reportError, t]);

  // Beim Projektwechsel die Canvas aus dem geladenen Anlagenschema wiederherstellen: platzierte
  // Symbole an ihren Positionen + die Verrohrung als Kanten. Die Katalog-Variablen kommen aus dem
  // separaten Katalog-Effekt (unten) — Schema (Anordnung) und Katalog (Variablen) sind entkoppelt
  // persistiert, hier aber über die Node-ID wieder zusammengeführt.
  useEffect(() => {
    if (!schematic) {
      setNodes([]);
      setEdges([]);
      setSelectedId(null);
      return;
    }
    const restored: Node<PlantNodeData>[] = schematic.nodes.map((n) => {
      const sym = symbolById[n.symbol];
      const label =
        n.label ?? (sym ? (lang === 'de' ? sym.label : sym.label_en) : n.symbol);
      return {
        id: n.id,
        type: 'plant',
        position: { x: n.position.x, y: n.position.y },
        data: {
          symbolId: n.symbol,
          label,
          category: sym?.category ?? 'behaelter',
          isaTag: sym?.isa_tag,
        },
      };
    });
    const restoredEdges: Edge[] = schematic.edges.map((e, i) => schematicEdge(e, i, lang));
    setNodes(restored);
    setEdges(restoredEdges);
    setSelectedId(null);
    // `symbolById` bewusst nicht in den Abhängigkeiten: das Schema ist die Wahrheit über die
    // Knoten; kommt später Symbol-Metadaten nach, aktualisiert der Katalog-/Render-Pfad die
    // Beschriftung ohnehin nicht sicherheitskritisch. Das vermeidet Canvas-Resets beim Nachladen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schematic, lang, setNodes, setEdges]);

  // Bereits vorhandene Katalog-Variablen des Projekts in die Draft-Liste übernehmen (ohne
  // Canvas-Instanz — sie erscheinen als „bereits gepflegt").
  useEffect(() => {
    if (catalog) {
      setVariables(catalog.variables.map((v) => ({ ...v, nodeId: v.source.symbol })));
    }
  }, [catalog]);

  const placeSymbol = useCallback(
    (sym: PaletteSymbol) => {
      const nodeId = `${sym.id}-${Date.now().toString(36)}`;
      const count = nodes.length;
      const node: Node<PlantNodeData> = {
        id: nodeId,
        type: 'plant',
        position: { x: 120 + (count % 4) * 170, y: 80 + Math.floor(count / 4) * 130 },
        data: {
          symbolId: sym.id,
          label: lang === 'de' ? sym.label : sym.label_en,
          category: sym.category,
          isaTag: sym.isa_tag,
        },
      };
      setNodes((prev) => [...prev, node]);
      // Sensor-/Aktor-Variablen als Katalog-Drafts anlegen.
      if (sym.variables && sym.variables.length > 0) {
        setVariables((prev) => [
          ...prev,
          ...sym.variables!.map((v, i) => draftFromVariable(sym, nodeId, v, prev.length + i)),
        ]);
      }
      setSelectedId(nodeId);
      setPaletteOpen(false);
    },
    [nodes.length, lang, setNodes],
  );

  const nodeVariables = useMemo(
    () => variables.filter((v) => v.nodeId === selectedId || v.source.symbol.endsWith(`@${selectedId}`)),
    [variables, selectedId],
  );

  const updateVariable = useCallback(
    (tag: string, patch: Partial<DraftVariable>) => {
      setVariables((prev) => prev.map((v) => (v.tag === tag ? { ...v, ...patch } : v)));
    },
    [],
  );

  const removeNode = useCallback(
    (nodeId: string) => {
      setNodes((prev) => prev.filter((n) => n.id !== nodeId));
      setVariables((prev) => prev.filter((v) => !v.source.symbol.endsWith(`@${nodeId}`)));
      setSelectedId(null);
    },
    [setNodes],
  );

  // Das Anlagenschema aus dem aktuellen Canvas-Zustand ableiten: platzierte Symbole mit ihren
  // Positionen und die Verrohrung (Kanten). Das ist die visuelle Darstellung, die beim nächsten
  // Projektwechsel die Canvas wieder aufbaut.
  const buildSchematic = useCallback((): PlantSchematic => {
    const schematicNodes: SchematicNode[] = nodes.map((n) => ({
      id: n.id,
      symbol: n.data.symbolId,
      label: n.data.label,
      position: { x: n.position.x, y: n.position.y },
    }));
    const nodeIds = new Set(schematicNodes.map((n) => n.id));
    // Nur Kanten behalten, deren beide Enden noch auf der Canvas stehen (Referenzintegrität; das
    // Backend würde eine verwaiste Kante sonst mit 422 zurückweisen).
    const schematicEdges = edges
      .filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target))
      .map((e) => {
        // Stromtyp aus data.stream (gestylte Kanten) oder – für manuell gezogene Kanten – aus dem
        // Label, sofern es ein reiner Stromtyp-Schlüssel ist.
        const fromData =
          e.data && typeof (e.data as { stream?: unknown }).stream === 'string'
            ? ((e.data as { stream?: string }).stream as string)
            : undefined;
        const stream = fromData ?? (typeof e.label === 'string' ? e.label : undefined);
        return {
          id: e.id,
          from: e.source,
          to: e.target,
          ...(stream ? { stream } : {}),
        };
      });
    return {
      schema_version: 1,
      project: activeProjectId ?? undefined,
      nodes: schematicNodes,
      edges: schematicEdges,
    };
  }, [nodes, edges, activeProjectId]);

  // Schema (Symbole + Positionen + Verrohrung) UND den daraus abgeleiteten Katalog persistieren.
  // Wir strippen das interne `nodeId`-Feld, bevor wir den Katalog gegen sein Schema schreiben. Die
  // Kopplung bleibt: der Katalog entsteht aus den Variablen der platzierten Symbole.
  const saveCatalog = useCallback(async () => {
    if (!activeProjectId) return;
    setSaving(true);
    // Das interne Feld `nodeId` (Canvas-Zuordnung) gehört nicht ins schema-konforme Dokument.
    const cleaned: CatalogVariable[] = variables.map((v) => {
      const clean: CatalogVariable = {
        tag: v.tag,
        name: v.name,
        quantity: v.quantity,
        unit: v.unit,
        range: v.range,
        direction: v.direction,
        source: v.source,
      };
      if (v.limits) clean.limits = v.limits;
      if (v.opc) clean.opc = v.opc;
      if (v.description) clean.description = v.description;
      return clean;
    });
    const doc: SignalCatalog = {
      schema_version: 1,
      project: activeProjectId,
      variables: cleaned,
    };
    const schematicDoc = buildSchematic();
    try {
      // Erst das Schema (visuelle Anordnung), dann den Katalog schreiben. Die Revision des
      // Katalog-Commits melden wir in der Statusleiste — der Schema-Commit läuft davor.
      await putSchematic(activeProjectId, schematicDoc);
      setSchematic(schematicDoc);
      const res = await putCatalog(activeProjectId, doc);
      setCatalog(doc);
      notify('success', t('plant.saved', { rev: res.rev }));
    } catch (err) {
      reportError(err, t('plant.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [
    activeProjectId,
    variables,
    buildSchematic,
    setCatalog,
    setSchematic,
    notify,
    reportError,
    t,
  ]);

  // Default-Regelmuster stempeln (Issue #21): aus den Variablen des gewählten Symbols den
  // vorverdrahteten Teilgraphen erzeugen (Backend, idempotent) und in den gespeicherten
  // Signalgraphen mergen. Das Backend speichert nicht — wir persistieren den Vorschlag bewusst per
  // PUT, damit die Signalgraph-Ansicht die Knoten sieht. Regelschleifen stehen dabei in Hand.
  const stampNode = useCallback(async () => {
    if (!activeProjectId || !selectedId) return;
    const node = nodes.find((n) => n.id === selectedId);
    if (!node) return;
    const nodeVars = variables.filter(
      (v) => v.nodeId === selectedId || v.source.symbol.endsWith(`@${selectedId}`),
    );
    if (nodeVars.length === 0) {
      notify('info', t('plant.stampNoVars'));
      return;
    }
    const symbolId = node.data.symbolId;
    const sym = symbols.find((s) => s.id === symbolId);
    const bindings = sym?.setpoint_bindings ?? [];

    const stampVars: StampVariable[] = nodeVars.map((v) => {
      const sv: StampVariable = {
        tag: v.tag,
        name: v.name,
        unit: v.unit,
        direction: v.direction,
        range: { min: v.range.min, max: v.range.max },
      };
      if (v.limits) sv.limits = v.limits;
      // Aktor-Sollwert: die kuratierten Regel-Defaults über die setpoint_id des Symbols treffen.
      if (v.direction === 'output' && bindings.length > 0) sv.setpoint_id = bindings[0];
      return sv;
    });

    setStamping(true);
    try {
      const res = await stampGraph(activeProjectId, {
        symbol_id: symbolId,
        node_ref: selectedId,
        variables: stampVars,
      });
      const addedCount = res.added.nodes.length;
      if (addedCount === 0) {
        notify('info', t('plant.stampNothing'));
        return;
      }
      // Den gemergten Vorschlag als Signalgraph persistieren (bewusstes Einsetzen).
      await putGraph(activeProjectId, res.graph);
      notify('success', t('plant.stamped', { added: addedCount, skipped: res.skipped.length }));
    } catch (err) {
      reportError(err, t('plant.stampFailed'));
    } finally {
      setStamping(false);
    }
  }, [activeProjectId, selectedId, nodes, variables, symbols, notify, reportError, t]);

  if (!activeProject) {
    return (
      <div className="p-8">
        <EmptyState title={t('common.noProject')} hint={t('common.noProjectHint')} />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col md:grid md:grid-cols-[16rem_1fr_20rem]">
      {/* Auf dem Handy holt diese Leiste die beiden weggeklappten Panele hervor. */}
      <CanvasDrawerBar
        testIdPrefix="plant"
        leftLabel={t('plant.palette')}
        rightLabel={t('plant.properties')}
        onOpenLeft={() => setPaletteOpen(true)}
        onOpenRight={() => setPropsOpen(true)}
      />

      {/* Palette */}
      <SidePanel
        side="left"
        testId="plant-palette-panel"
        title={t('plant.palette')}
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
      >
        <DomainFilterChips
          domains={availableDomains}
          selected={domainFilter}
          onToggle={(domain) =>
            setDomainFilter((prev) =>
              prev.includes(domain) ? prev.filter((d) => d !== domain) : [...prev, domain],
            )
          }
          onReset={() => setDomainFilter([])}
        />
        <input
          type="search"
          data-testid="plant-palette-search"
          value={paletteSearch}
          onChange={(e) => setPaletteSearch(e.target.value)}
          placeholder={t('plant.search')}
          className="rounded border border-bf-border bg-bf-navy-light px-2 py-1.5 text-sm text-bf-white placeholder:text-bf-muted focus:border-bf-gold focus:outline-none"
        />
        {loadingPalette && <p className="text-xs text-bf-muted">{t('common.loading')}</p>}
        {!loadingPalette && symbols.length === 0 && (
          <p className="text-xs text-bf-muted">{t('plant.paletteEmpty')}</p>
        )}
        {!loadingPalette && symbols.length > 0 && groupedSymbols.length === 0 && (
          <p className="text-xs text-bf-muted" data-testid="plant-palette-noresults">
            {t('plant.searchEmpty')}
          </p>
        )}
        <div className="flex flex-col gap-3" data-testid="plant-palette">
          {groupedSymbols.map(({ category, items }) => (
            <div key={category} className="flex flex-col gap-1">
              <span
                className="text-[10px] uppercase tracking-wider text-bf-muted"
                data-testid={`plant-cat-${category}`}
              >
                {t(`plant.category.${category}`)}
              </span>
              {items.map((sym) => (
                <button
                  key={sym.id}
                  type="button"
                  data-testid={`plant-symbol-${sym.id}`}
                  onClick={() => placeSymbol(sym)}
                  className="flex items-center gap-2 rounded border border-bf-border px-2 py-1.5 text-left text-sm text-bf-white transition-colors hover:border-bf-gold"
                >
                  {/* Erkennbares P&ID-Icon je Symbol (Kategorie-Fallback), Gold-Strich im CI. */}
                  <PlantSymbolIcon
                    symbolId={sym.id}
                    category={sym.category}
                    title={lang === 'de' ? sym.label : sym.label_en}
                    className="h-6 w-6 shrink-0 text-bf-gold"
                  />
                  <span className="flex-1 truncate">{lang === 'de' ? sym.label : sym.label_en}</span>
                  <span className="shrink-0 text-[10px] text-bf-muted">
                    {sym.isa_tag ?? sym.category}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </SidePanel>

      {/* Canvas */}
      <div className="relative min-h-0 flex-1 md:flex-none">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          onNodeClick={(_, n) => setSelectedId(n.id)}
          onPaneClick={() => setSelectedId(null)}
          fitView
          // Ohne niedrigen minZoom klemmt der React-Flow-Default (0,5) das Einrahmen breiter
          // Schemata auf schmalen Schirmen (Issue #479).
          minZoom={0.05}
          // Einrahmen beim Öffnen: am Schreibtisch die Übersicht über den ganzen Graphen. Auf dem
          // Handy würde derselbe Ausschnitt die Beschriftungen zu einem Schmierstreifen stauchen,
          // deshalb dort ein Zoom-Boden — der Graph ist dann lesbar und wird geschoben. Die
          // Übersicht bleibt über den „Einpassen"-Knopf der Steuerleiste einen Tipp entfernt.
          fitViewOptions={{ padding: 0.15, minZoom: narrow ? 0.5 : undefined }}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#1A3460" gap={20} />
          <Controls className="!bg-bf-navy-light !text-bf-white" />
        </ReactFlow>
        {nodes.length === 0 && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-8">
            <EmptyState title={t('plant.canvasEmpty')} hint={t('plant.canvasHint')} />
          </div>
        )}
      </div>

      {/* Eigenschaften */}
      <SidePanel
        side="right"
        testId="plant-properties-panel"
        title={t('plant.properties')}
        open={propsOpen}
        onClose={() => setPropsOpen(false)}
      >
        <div className="flex flex-wrap items-center gap-2">
          {activeProjectId && (
            <span data-testid="plant-onboarding-wizard-open">
              <Button variant="secondary" onClick={() => setWizardOpen(true)}>
                {t('plant.onboardingWizardOpen')}
              </Button>
            </span>
          )}
          {activeProjectId && (
            <span data-testid="plant-ua-browse-open">
              <Button variant="secondary" onClick={() => setUaBrowseOpen(true)}>
                {t('plant.uaBrowseOpen')}
              </Button>
            </span>
          )}
          <span data-testid="plant-save-catalog">
            <Button
              variant="primary"
              onClick={saveCatalog}
              disabled={saving || (nodes.length === 0 && variables.length === 0)}
            >
              {t('plant.saveSchematic')}
            </Button>
          </span>
        </div>

        {!selectedId && (
          <EmptyState title={t('plant.selectNode')} hint={t('plant.selectNodeHint')} />
        )}

        {selectedId && nodeVariables.length === 0 && (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-bf-muted">{t('plant.noVariables')}</p>
            <Button variant="danger" onClick={() => removeNode(selectedId)}>
              {t('plant.removeNode')}
            </Button>
          </div>
        )}

        {selectedId &&
          nodeVariables.map((v) => (
            <div
              key={v.tag}
              data-testid="plant-variable"
              className="flex flex-col gap-2 rounded border border-bf-border p-2"
            >
              <div className="text-xs font-semibold text-bf-white">
                {v.name} · {v.quantity}
              </div>
              <span data-testid="plant-var-tag">
                <LabeledInput
                  label={t('plant.tag')}
                  value={v.tag}
                  onChange={(val) => updateVariable(v.tag, { tag: val })}
                />
              </span>
              <div className="grid grid-cols-2 gap-2">
                <LabeledInput
                  label={t('plant.unit')}
                  value={v.unit}
                  onChange={(val) => updateVariable(v.tag, { unit: val })}
                />
                <span data-testid="plant-var-direction">
                <LabeledSelect
                  label={t('plant.direction')}
                  value={v.direction}
                  onChange={(val) => updateVariable(v.tag, { direction: val as Direction })}
                >
                  <option value="input">{t('plant.input')}</option>
                  <option value="output">{t('plant.output')}</option>
                </LabeledSelect>
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <LabeledInput
                  label={t('plant.rangeMin')}
                  type="number"
                  value={String(v.range.min)}
                  onChange={(val) =>
                    updateVariable(v.tag, { range: { ...v.range, min: Number(val) } })
                  }
                />
                <LabeledInput
                  label={t('plant.rangeMax')}
                  type="number"
                  value={String(v.range.max)}
                  onChange={(val) =>
                    updateVariable(v.tag, { range: { ...v.range, max: Number(val) } })
                  }
                />
              </div>
              <LabeledInput
                label={t('plant.opcNode')}
                value={v.opc?.node_id ?? ''}
                placeholder="ns=2;s=FT101.PV"
                onChange={(val) =>
                  updateVariable(v.tag, {
                    opc: val
                      ? { node_id: val, datatype: v.opc?.datatype ?? 'Float' }
                      : undefined,
                  })
                }
              />
              {v.opc && (
                <>
                  <LabeledSelect
                    label={t('plant.opcType')}
                    value={v.opc.datatype}
                    onChange={(val) =>
                      updateVariable(v.tag, {
                        opc: { ...v.opc!, datatype: val as OpcDatatype },
                      })
                    }
                  >
                    {DATATYPES.map((dt) => (
                      <option key={dt} value={dt}>
                        {dt}
                      </option>
                    ))}
                  </LabeledSelect>
                  <LabeledInput
                    label={t('plant.opcNamespace')}
                    value={v.opc.namespace ?? ''}
                    placeholder="urn:blackfortflow:klaeranlage-demo"
                    onChange={(val) => {
                      // Leeres Feld = namespace weglassen (Schema: additionalProperties:false,
                      // optionales Feld — keinen leeren String schreiben).
                      const next = { ...v.opc! };
                      if (val.trim()) next.namespace = val;
                      else delete next.namespace;
                      updateVariable(v.tag, { opc: next });
                    }}
                  />
                  <LabeledSelect
                    label={t('plant.opcAccess')}
                    value={v.opc.access ?? ''}
                    onChange={(val) => {
                      // Leere Auswahl = access weglassen (enum lässt keinen leeren String zu).
                      const next = { ...v.opc! };
                      if (val) next.access = val as OpcAccess;
                      else delete next.access;
                      updateVariable(v.tag, { opc: next });
                    }}
                  >
                    <option value="">{t('plant.opcUnset')}</option>
                    <option value="read">{t('plant.opcAccessRead')}</option>
                    <option value="write">{t('plant.opcAccessWrite')}</option>
                    <option value="read_write">{t('plant.opcAccessReadWrite')}</option>
                  </LabeledSelect>
                  <LabeledSelect
                    label={t('plant.opcQuality')}
                    value={v.opc.quality_handling ?? ''}
                    onChange={(val) => {
                      // Leere Auswahl = quality_handling weglassen (Backend-Default: gate).
                      const next = { ...v.opc! };
                      if (val) next.quality_handling = val as OpcQualityHandling;
                      else delete next.quality_handling;
                      updateVariable(v.tag, { opc: next });
                    }}
                  >
                    <option value="">{t('plant.opcUnset')}</option>
                    <option value="gate">{t('plant.opcQualityGate')}</option>
                    <option value="passthrough">{t('plant.opcQualityPassthrough')}</option>
                    <option value="hold_last">{t('plant.opcQualityHoldLast')}</option>
                  </LabeledSelect>
                </>
              )}
            </div>
          ))}

        {selectedId && nodeVariables.length > 0 && (
          <div className="flex flex-col gap-2 rounded border border-bf-gold/40 bg-bf-gold/5 p-2">
            <p className="text-xs text-bf-muted">{t('plant.stampHint')}</p>
            <span data-testid="plant-stamp">
              <Button variant="primary" onClick={stampNode} disabled={stamping}>
                {t('plant.stampDefaults')}
              </Button>
            </span>
          </div>
        )}

        {selectedId && nodeVariables.length > 0 && (
          <Button variant="danger" onClick={() => removeNode(selectedId)}>
            {t('plant.removeNode')}
          </Button>
        )}
      </SidePanel>

      {uaBrowseOpen && activeProjectId && (
        <UaBrowseOnboardingDialog
          projectId={activeProjectId}
          onClose={() => setUaBrowseOpen(false)}
          onConfirmed={(rev) => {
            notify('success', t('plant.uaBrowse.saved', { rev }));
            // Vollen Projekt-Reload auslösen: der geschriebene Katalog kommt frisch aus dem Backend.
            bumpReload();
          }}
        />
      )}

      {wizardOpen && activeProjectId && (
        <OnboardingWizardDialog
          projectId={activeProjectId}
          onClose={() => setWizardOpen(false)}
          onConfirmed={(rev) => {
            notify('success', t('onboardingWizard.saved', { rev }));
            // Voller Projekt-Reload: der geschriebene Katalog kommt frisch aus dem Backend.
            bumpReload();
          }}
        />
      )}
    </div>
  );
}

// ── Domänen-Filterleiste der Symbol-Palette (Issue #330) ─────────────────────────────
//
// Chips statt Dropdown: mit acht und mehr Domänen ist die Mehrfachauswahl der Normalfall (eine
// Brauerei mit Fernwärme-Anbindung braucht zwei), und ein Klick auf einen Chip ist kürzer als
// zwei auf ein Auswahlfeld. Die Liste kommt aus dem Symbolkatalog — eine neue Domäne erscheint
// hier, ohne dass das Frontend sie kennen muss.
function DomainFilterChips({
  domains,
  selected,
  onToggle,
  onReset,
}: {
  domains: string[];
  selected: string[];
  onToggle: (domain: string) => void;
  onReset: () => void;
}): JSX.Element | null {
  const { t } = useI18n();
  // Ohne Symbole (oder ohne Domänen-Angaben) gibt es nichts zu filtern — dann keine leere Leiste.
  if (domains.length === 0) return null;

  // Für eine dem Frontend unbekannte Domäne fällt `t` auf den Key zurück; dann ist die reine
  // Kennung die bessere Anzeige als "domain.xyz".
  const label = (domain: string): string => {
    const key = `domain.${domain}`;
    const translated = t(key);
    return translated === key ? domain : translated;
  };

  const chip = (active: boolean): string =>
    'rounded-full border px-2 py-0.5 text-xs transition-colors ' +
    (active
      ? 'border-bf-gold bg-bf-gold/20 text-bf-gold'
      : 'border-bf-border text-bf-muted hover:border-bf-gold hover:text-bf-white');

  return (
    <div className="flex flex-col gap-1" data-testid="plant-domain-filter">
      <span className="text-[10px] uppercase tracking-wider text-bf-muted">
        {t('plant.domain')}
      </span>
      <div className="flex flex-wrap gap-1">
        <button
          type="button"
          data-testid="plant-domain-all"
          aria-pressed={selected.length === 0}
          onClick={onReset}
          className={chip(selected.length === 0)}
        >
          {t('plant.allDomains')}
        </button>
        {domains.map((domain) => (
          <button
            key={domain}
            type="button"
            data-testid={`plant-domain-${domain}`}
            aria-pressed={selected.includes(domain)}
            onClick={() => onToggle(domain)}
            className={chip(selected.includes(domain))}
          >
            {label(domain)}
          </button>
        ))}
      </div>
    </div>
  );
}
